import { NavLink } from "react-router-dom";
import { PlateSearch } from "./PlateSearch";
import { useAuth } from "../lib/auth";
import {
  IconHome, IconDollar, IconTruck, IconLineChart,
  IconCashflow, IconLightbulb, IconDocument, IconWhatsApp,
  IconShield, IconSync, IconCog, IconSparkle, IconFuel, IconTire, IconWrench, IconUser, IconClipboard, IconCalendar, IconBarChart,
} from "./icons";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | number;
  end?: boolean;
}

const MAIN_NAV: NavItem[] = [
  { to: "/",              label: "Visão Executiva",   icon: IconHome,        end: true },
  { to: "/financeiro",    label: "Financeiro",         icon: IconDollar },
  { to: "/operacional",   label: "Operacional CT-e",   icon: IconTruck },
  { to: "/dre",           label: "DRE / Resultado",    icon: IconLineChart },
  { to: "/fluxo-caixa",   label: "Fluxo de Caixa",    icon: IconCashflow },
];

const FROTA_NAV: NavItem[] = [
  { to: "/frota",             label: "Visão Geral", icon: IconTruck, end: true },
  { to: "/frota/combustivel", label: "Combustível", icon: IconFuel },
  { to: "/frota/pneus",       label: "Pneus",       icon: IconTire },
  { to: "/frota/manutencao",  label: "Manutenção",  icon: IconWrench, end: true },
  { to: "/frota/manutencao/gestao", label: "Gestão da Manutenção", icon: IconBarChart },
  { to: "/frota/manutencao/checklists", label: "Checklists", icon: IconDocument },
  { to: "/frota/manutencao/nao-conformidades", label: "Não Conformidades", icon: IconShield },
  { to: "/frota/manutencao/ordens", label: "Ordens de Serviço", icon: IconClipboard },
  { to: "/frota/manutencao/erp", label: "OS do ERP", icon: IconWrench },
  { to: "/frota/manutencao/preventiva", label: "Plano Preventivo", icon: IconCalendar },
  { to: "/frota/manutencao/grupos", label: "Grupos de Veículos", icon: IconTruck },
  { to: "/frota/manutencao/modelos", label: "Modelos de Checklist", icon: IconClipboard },
  { to: "/frota/custo-placa", label: "Custo por Placa",  icon: IconDollar },
  { to: "/frota/margem",      label: "Margem por Placa", icon: IconLineChart },
  { to: "/frota/pedagio",     label: "Pedágio",     icon: IconDollar },
  { to: "/frota/seguro",      label: "Seguro de Carga",  icon: IconShield },
  { to: "/frota/motoristas",  label: "Motoristas",  icon: IconUser },
];

const INTEL_NAV: NavItem[] = [
  { to: "/inteligencia",  label: "Insights e Alertas", icon: IconLightbulb },
];

const CHANNEL_NAV: NavItem[] = [
  { to: "/whatsapp",      label: "WhatsApp",           icon: IconWhatsApp },
];

/** Na demonstração não há canal de WhatsApp: a conversa roda pela tela de perguntas (mesmo motor, sem envio). */
const CHANNEL_NAV_DEMO: NavItem[] = [
  { to: "/perguntas", label: "Perguntar ao SIAG", icon: IconSparkle },
];

const ADMIN_NAV: NavItem[] = [
  { to: "/qualidade-dados", label: "Qualidade dos Dados", icon: IconShield },
  { to: "/sincronizacao",   label: "Sincronização",        icon: IconSync },
  { to: "/configuracoes",   label: "Configurações",        icon: IconCog },
];

/** Na empresa de demonstração só a leitura de qualidade dos dados fica visível;
 *  sincronização e configurações expõem integrações (a API também nega). */
const ADMIN_NAV_DEMO: NavItem[] = ADMIN_NAV.filter((i) => i.to === "/qualidade-dados");

function NavGroup({ items }: { items: NavItem[] }) {
  return (
    <nav className="space-y-0.5">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors group ${
              isActive
                ? "bg-tm-500/20 text-tm-100 font-medium"
                : "text-tm-100/60 hover:bg-white/5 hover:text-tm-50"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <item.icon className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-tm-400" : "text-tm-100/50 group-hover:text-tm-100/80"}`} />
              <span className="truncate">{item.label}</span>
              {item.badge !== undefined && (
                <span className="ml-auto bg-tm-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center">
                  {item.badge}
                </span>
              )}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

export function Sidebar() {
  const { user } = useAuth();
  return (
    <aside className="fixed inset-y-0 left-0 w-64 bg-tm-900 flex flex-col z-40 select-none">
      {/* Logo */}
      <div className="px-4 py-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-tm-500 grid place-items-center flex-shrink-0">
            <IconSparkle className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-sm font-bold text-white leading-tight">{user?.isDemo && user.companyName ? user.companyName.toUpperCase() : "TRANSMASI"}</div>
            <div className="text-xs text-tm-300 leading-tight mt-0.5">Inteligência de Negócios</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        <div>
          <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-tm-100/30">Painéis</p>
          <NavGroup items={MAIN_NAV} />
        </div>

        <div>
          <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-tm-100/30">Frota</p>
          <PlateSearch />
          <NavGroup items={FROTA_NAV} />
        </div>

        <div>
          <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-tm-100/30">Inteligência</p>
          <NavGroup items={INTEL_NAV} />
        </div>

        <div>
          <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-tm-100/30">Canal</p>
          <NavGroup items={user?.isDemo ? CHANNEL_NAV_DEMO : CHANNEL_NAV} />
        </div>

        <div>
          <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-tm-100/30">Sistema</p>
          <NavGroup items={user?.isDemo ? ADMIN_NAV_DEMO : ADMIN_NAV} />
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-white/10">
        {user?.isDemo && (
          <p className="mb-2 inline-block rounded bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-tm-100/70">
            Ambiente de Demonstração
          </p>
        )}
        <p className="text-xs text-tm-100/30 leading-tight">Powered by</p>
        <p className="text-xs text-tm-100/50 font-medium">Sistólica · Inteligência SIAG</p>
      </div>
    </aside>
  );
}
